import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ReactCompareSlider, ReactCompareSliderImage } from "react-compare-slider";
import { Check, ArrowRight, Wand2, Loader2, ImagePlus, Lock } from "lucide-react";
import { Header } from "@/components/Header";

const BEFORE = "/demo-before.jpg";
const AFTER = "/demo-after.jpg";

const DOORS = [
  { key: "coronado", name: "Coronado", img: "/demo-doors/coronado.jpg" },
  { key: "manhattan", name: "Manhattan", img: "/demo-doors/manhattan.jpg" },
  { key: "cambridge", name: "Cambridge", img: "/demo-doors/cambridge.jpg" },
  { key: "lakeshore", name: "Lakeshore", img: "/demo-doors/lakeshore.jpg" },
  { key: "shasta", name: "Shasta", img: "/demo-doors/shasta.jpg" },
  { key: "camden", name: "Camden", img: "/demo-doors/camden.jpg" },
];

const COLORS = [
  { key: "white", name: "Bright White", hex: "#F5F3EE" },
  { key: "dover", name: "Dover White", hex: "#E7E1D3" },
  { key: "sage", name: "Sage", hex: "#8A9A82" },
  { key: "navy", name: "Navy", hex: "#2C3A4B" },
  { key: "charcoal", name: "Charcoal", hex: "#3C3C3C" },
];

const HARDWARE = [
  { key: "gold", name: "Polished Gold", css: "linear-gradient(135deg,#f5d78e,#c99a3a)" },
  { key: "nickel", name: "Brushed Nickel", css: "linear-gradient(135deg,#e6e6e6,#a8a8a8)" },
  { key: "black", name: "Matte Black", css: "linear-gradient(135deg,#3a3a3a,#111)" },
];

const PROMPT = "Reface all cabinets in bright white with flat-panel upper doors, add polished gold bar pulls, and replace the beverage fridge with a matching white three-drawer stack. Keep the same layout, granite counters and wall ovens.";

export default function Demo() {
  const [door, setDoor] = useState("camden");
  const [color, setColor] = useState("white");
  const [hw, setHw] = useState("gold");
  const [generating, setGenerating] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [pressing, setPressing] = useState(false);
  const timer = useRef();

  useEffect(() => () => clearTimeout(timer.current), []);

  const generate = () => {
    setRevealed(false);
    setGenerating(true);
    timer.current = setTimeout(() => { setGenerating(false); setRevealed(true); }, 2200);
  };
  const restart = () => { generate(); }; // eslint-disable-line no-unused-vars

  // Auto-run the reface once, ~2s after load: press animation → loading → reveal.
  useEffect(() => {
    const t = setTimeout(() => {
      setPressing(true);
      setTimeout(() => { setPressing(false); generate(); }, 280);
    }, 2000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <div className="mx-auto max-w-6xl px-6 pb-16 pt-8 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="overline">Live demo</p>
            <h1 className="mt-1 font-display text-3xl tracking-tight md:text-4xl">See it in action</h1>
            <p className="mt-2 max-w-xl text-sm text-[var(--ink-soft)]">
              A real customer photo, pre-loaded — watch it reface automatically.
            </p>
          </div>
          <Link to="/signup" data-testid="demo-signup-btn"
            className="flex items-center gap-2 rounded-full bg-[var(--brand)] px-6 py-3 text-sm font-semibold text-white transition-transform duration-200 ease-out hover:-translate-y-px hover:bg-[var(--brand-dark)]">
            Create your account <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[360px_1fr]">
          {/* Configurator */}
          <aside className="rounded-xl border border-[var(--line)] bg-white p-5">
            {/* Cabinet Design */}
            <div>
              <p className="overline">Step 01</p>
              <h2 className="mt-1 font-display text-xl tracking-tight">Cabinet design</h2>
              <div className="mt-3 grid grid-cols-3 gap-2.5" data-testid="demo-door-grid">
                {DOORS.map((d) => {
                  const avail = d.key === "camden";
                  const on = door === d.key;
                  return (
                    <button key={d.key} data-testid={`demo-door-${d.key}`} disabled={!avail} onClick={() => avail && setDoor(d.key)} title={avail ? d.name : "Not Available In Demo"}
                      className={`group relative overflow-hidden rounded-lg border bg-white transition-transform duration-200 ease-out ${avail ? "hover:-translate-y-px" : "cursor-not-allowed"} ${on ? "border-[var(--brand)] ring-2 ring-[var(--brand)] ring-offset-1" : "border-[var(--line)]"}`}>
                      <img src={d.img} alt={d.name} className={`aspect-[3/4] w-full bg-white object-contain p-1.5 ${avail ? "" : "opacity-30 grayscale"}`} />
                      {avail ? (
                        <span className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-1.5 py-1 text-[10px] font-medium text-white">{d.name}</span>
                      ) : (
                        <span className="absolute inset-0 flex items-center justify-center px-2">
                          <span className="rounded-md bg-black/70 px-2 py-1 text-center text-[9px] font-semibold leading-tight text-white">Not Available In Demo</span>
                        </span>
                      )}
                      {on && <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--brand)] text-white"><Check className="h-2.5 w-2.5" strokeWidth={3} /></span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Cabinet Color */}
            <div className="mt-6">
              <p className="overline">Step 02</p>
              <h2 className="mt-1 font-display text-xl tracking-tight">Cabinet color</h2>
              <div className="mt-3 flex flex-wrap gap-3" data-testid="demo-color-row">
                {COLORS.map((c) => {
                  const avail = c.key === "white";
                  const on = color === c.key;
                  return (
                    <button key={c.key} data-testid={`demo-color-${c.key}`} disabled={!avail} onClick={() => avail && setColor(c.key)} title={avail ? c.name : "Not Available In Demo"}
                      className={`relative h-10 w-10 rounded-full transition-transform duration-200 ease-out ${avail ? "hover:-translate-y-px" : "cursor-not-allowed opacity-40"} ${on ? "ring-2 ring-[var(--brand)] ring-offset-2" : "ring-1 ring-[var(--line)]"}`}
                      style={{ backgroundColor: c.hex }}>
                      {on && <Check className="absolute inset-0 m-auto h-4 w-4 text-white mix-blend-difference" strokeWidth={3} />}
                      {!avail && <Lock className="absolute inset-0 m-auto h-3.5 w-3.5 text-black/50" strokeWidth={2} />}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-[var(--ink-soft)]">Bright White · Other Colors Not Available In Demo</p>
            </div>

            {/* Hardware */}
            <div className="mt-6">
              <p className="overline">Step 03</p>
              <h2 className="mt-1 font-display text-xl tracking-tight">Hardware</h2>
              <div className="mt-3 flex flex-wrap gap-3" data-testid="demo-hardware-row">
                {HARDWARE.map((h) => {
                  const avail = h.key === "gold";
                  const on = hw === h.key;
                  return (
                    <button key={h.key} data-testid={`demo-hardware-${h.key}`} disabled={!avail} onClick={() => avail && setHw(h.key)} title={avail ? h.name : "Not Available In Demo"}
                      className={`relative h-10 w-10 rounded-full transition-transform duration-200 ease-out ${avail ? "hover:-translate-y-px" : "cursor-not-allowed opacity-40"} ${on ? "ring-2 ring-[var(--brand)] ring-offset-2" : "ring-1 ring-black/10"}`}
                      style={{ background: h.css }}>
                      {on && <Check className="absolute inset-0 m-auto h-4 w-4 text-white mix-blend-difference" strokeWidth={3} />}
                      {!avail && <Lock className="absolute inset-0 m-auto h-3.5 w-3.5 text-white/70" strokeWidth={2} />}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-[var(--ink-soft)]">Polished Gold · Other Finishes Not Available In Demo</p>
            </div>

            {/* AI prompt */}
            <div className="mt-6">
              <p className="overline mb-2 flex items-center gap-1.5"><Wand2 className="h-3.5 w-3.5 text-[var(--brand)]" /> AI-guided prompt</p>
              <div data-testid="demo-prompt" className="rounded-lg border border-[var(--line)] bg-[var(--surface-2)] p-3 text-sm leading-relaxed text-[var(--ink)]">
                {PROMPT}
              </div>
            </div>
          </aside>

          {/* Stage */}
          <div>
            <p className="overline">Presentation</p>
            <h2 className="font-display text-2xl tracking-tight">{revealed ? "The reveal" : "The vision"}</h2>
            <div className="mt-3 overflow-hidden rounded-xl border border-[var(--line)] shadow-sm" data-testid="demo-stage">
              {generating ? (
                <div className="flex aspect-[1264/848] w-full animate-pulse flex-col items-center justify-center gap-4 bg-[var(--bone)] text-[var(--ink-soft)]">
                  <Loader2 className="h-8 w-8 animate-spin text-[var(--brand)]" />
                  <p className="text-sm font-medium">Designing the new look…</p>
                </div>
              ) : revealed ? (
                <ReactCompareSlider data-testid="demo-slider" onlyHandleDraggable={false}
                  style={{ touchAction: "none" }}
                  itemOne={<ReactCompareSliderImage src={BEFORE} alt="Before reface" style={{ objectFit: "contain" }} />}
                  itemTwo={<ReactCompareSliderImage src={AFTER} alt="After reface" style={{ objectFit: "contain" }} />} />
              ) : (
                <img src={BEFORE} alt="Customer kitchen before" className="w-full" />
              )}
            </div>
            <p className="mt-3 flex items-center gap-2 text-sm text-[var(--ink-soft)]">
              <ImagePlus className="h-4 w-4 text-[var(--brand)]" />
              {revealed ? "Drag the slider to compare before & after." : "This is the customer's real kitchen — refacing automatically…"}
            </p>

            {/* Generate — under the preview, auto-pressed on load */}
            {!revealed && (
              <div className="mt-4">
                <button data-testid="demo-generate-btn" onClick={generate} disabled={generating}
                  className={`flex w-full items-center justify-center gap-2 rounded-full bg-[var(--brand)] py-3.5 text-sm font-semibold text-white transition-transform duration-150 ease-out hover:-translate-y-px hover:bg-[var(--brand-dark)] disabled:opacity-80 ${pressing ? "scale-95" : ""}`}>
                  {generating ? (<><Loader2 className="h-4 w-4 animate-spin" /> Rendering…</>) : (<><Wand2 className="h-4 w-4" strokeWidth={2} /> Generate render</>)}
                </button>
              </div>
            )}

            {revealed && (
              <div className="mt-5 flex flex-col gap-3 rounded-xl border border-[var(--brand)]/40 bg-[var(--brand)]/[0.06] p-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-display text-lg tracking-tight">Do this with your own projects</p>
                  <p className="text-sm text-[var(--ink-soft)]">Upload any photo and close the sale on the spot.</p>
                </div>
                <Link to="/signup" data-testid="demo-cta-signup"
                  className="flex shrink-0 items-center justify-center gap-2 rounded-full bg-[var(--brand)] px-6 py-3 text-sm font-semibold text-white transition-transform duration-200 ease-out hover:-translate-y-px hover:bg-[var(--brand-dark)]">
                  Start free <ArrowRight className="h-4 w-4" strokeWidth={2} />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
